﻿import {
    DotLegalButton,
    DotLegalCheckbox,
    DotLegalDatePicker,
    DotLegalDialog,
    DotLegalHeader,
    DotLegalMultiSelect,
    DotLegalRadioButton,
    DotLegalSelect,
    DotLegalTextField,
    DotLegalTimePicker,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { Box, Collapse, RadioGroup, Stack } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useCreateEditIncidentLogDialogDataMapping } from "./CreateEditIncidentLogDialog.hooks";
import DotLegalTextFieldWithSnippets from "../../common/components/dotLegalTextFieldWithSnippets/DotLegalTextFieldWithSnippets";
import { useCreateEditIncidentStyles } from "./CreateEditIncidentLogDialog.styles";
import TagsMultiSelect from "../../common/components/tagsMultiSelect/TagsMultiSelect";
import { EntityType } from "../../common/Common.types";
import { TagRelationType } from "../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { GetIncidentLogPlanLimit } from "../../auth/planProvider/PlanProvider.hooks.ts";
import TrialUsageWarning from "../../common/components/TrialUsageWarning.tsx";
import MentionInput from "../../common/components/mentionInput/MentionInput.tsx";
import DotLegalDocumentUploadWrapper from "../../common/components/DotLegalDocumentUploadWrapper.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface ICreateEditIncidentDialogProps {
    closeDialog: () => void;
    onSave: () => void;
    id?: string | undefined;
    isPdf?: boolean;
}

function CreateEditIncidentLogDialog(props: ICreateEditIncidentDialogProps) {
    const { translateString, getDateTimePickerLanguage, translateTime } = useTranslation();
    const { permissions } = useUserContext();
    const location = useLocation();
    const {
        saveModel,
        setSaveModel,
        dataCategoriesData,
        validateAndSave,
        consequenceSuggestions,
        securityMeasures,
        incidentTypes,
        isEdit,
        isLoading,
        isSaving,
        severityLevelSelectOptions,
        documentUploadModel,
        setDocumentUploadModel,
        validation,
        setShowAddDocument,
        showAddDocument,
        setDocumentName,
        isUploadingDocument,
        uploadPercentage,
    } = useCreateEditIncidentLogDialogDataMapping(props);
    const styles = useCreateEditIncidentStyles();
    const incidentLogPlanLimit = GetIncidentLogPlanLimit();

    const getMinDate = () => {
        return new Date(2015, 0, 1);
    };
    const getMaxDate = () => {
        return new Date();
    };
    const datePickerLanguage = getDateTimePickerLanguage();

    const getTimezoneFromUrl = () => {
        const urlParams = new URLSearchParams(location.search);
        return Number(urlParams.get("timezoneOffset")) || undefined;
    };

    const formatTimeWithTimezone = (time: Date | null) => {
        if (!time) return time;

        const timezoneOffset = getTimezoneFromUrl();

        return translateTime(time, timezoneOffset);
    };

    const bottomRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (showAddDocument && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [showAddDocument]);

    let content = (
        <Box sx={styles.container}>
            {props.isPdf && (
                <DotLegalHeader headerStyle={"xxxl"}>
                    {translateString("incident")}: {saveModel?.name}
                </DotLegalHeader>
            )}
            <Box sx={styles.subContainer}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentNameDescription")}>
                    {translateString("incidentName")}
                </DotLegalHeader>
                <DotLegalTextField
                    label={""}
                    placeholder={translateString("incidentNameTextFieldPlaceholder")}
                    value={saveModel?.name}
                    debounce={false}
                    onChange={(value) => {
                        let tempModel = { ...saveModel, name: value };
                        setSaveModel(tempModel);
                    }}
                    errorText={validation.getErrorsForDisplay("name")}
                    noMargin
                    isPdf={props.isPdf}
                    autoFocus
                />
            </Box>
            <Box sx={styles.subContainer}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentTimeDescription")}>
                    {translateString("incidentTime")}
                </DotLegalHeader>
                <Stack direction={"row"}>
                    <Box sx={{ mr: 1, flex: 1 }}>
                        <DotLegalDatePicker
                            label=""
                            noMargin
                            value={saveModel?.dateOfIncident}
                            onChange={(value) => {
                                let tempModel = { ...saveModel, dateOfIncident: value };
                                tempModel.unknownDateOfIncident = false;
                                setSaveModel(tempModel);
                            }}
                            view={"day"}
                            minDate={getMinDate()}
                            maxDate={getMaxDate()}
                            language={datePickerLanguage}
                        />
                    </Box>
                    <Box sx={{ ml: 1, flex: 1 }}>
                        {props.isPdf ? (
                            <DotLegalTextField
                                label=""
                                value={formatTimeWithTimezone(saveModel?.timeOfIncident) || ""}
                                onChange={() => {}}
                                noMargin
                                isPdf={props.isPdf}
                                disabled
                                debounce={false}
                            />
                        ) : (
                            <DotLegalTimePicker
                                value={saveModel?.timeOfIncident || null}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel };
                                    tempModel.unknownDateOfIncident = false;
                                    tempModel.timeOfIncident = value;
                                    setSaveModel(tempModel);
                                }}
                                language={datePickerLanguage}
                            />
                        )}
                    </Box>
                </Stack>
                <DotLegalCheckbox
                    label={translateString("incidentTimeUnknown")}
                    checked={saveModel?.unknownDateOfIncident}
                    onChange={(value) => {
                        let tempModel = { ...saveModel, unknownDateOfIncident: value };
                        if (value) {
                            tempModel.dateOfIncident = null;
                            tempModel.timeOfIncident = null;
                        }
                        setSaveModel(tempModel);
                    }}
                    margin={{
                        marginLeft: -1,
                        marginBottom: -1,
                        marginRight: 0,
                        marginTop: 0,
                    }}
                />
            </Box>
            <Box sx={styles.subContainer} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentCauseDescription")}>
                    {translateString("incidentCause")}
                </DotLegalHeader>
                <MentionInput
                    mentionSource={{ entityFromId: "", entityFromType: EntityType.IncidentLog }}
                    placeholder={translateString("incidentCauseTextFieldPlaceholder")}
                    value={saveModel?.cause ?? ""}
                    debounce={false}
                    multiline
                    rows={props.isPdf ? undefined : 3}
                    onChange={(value, relations) => {
                        let tempModel = {
                            ...saveModel,
                            cause: value,
                            relations: relations.map((x) => {
                                return {
                                    fromEntityId: props.id,
                                    fromEntityType: EntityType.IncidentLog,
                                    toEntityType: x.entityType,
                                    toEntityId: x.entityId,
                                };
                            }),
                        };
                        setSaveModel(tempModel);
                    }}
                    noMargin
                    manageRelationsExternal
                />
                {permissions.canAccessTags && (
                    <TagsMultiSelect
                        isLoading={isLoading}
                        entityType={TagRelationType.IncidentLog}
                        onChange={(tags) => {
                            let tempViewModel = { ...saveModel };
                            tempViewModel.tags = tags;
                            setSaveModel(tempViewModel);
                        }}
                        selectedTags={saveModel?.tags ?? []}
                    />
                )}
            </Box>

            <Box sx={styles.subContainer} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentTypeDescription")}>
                    {translateString("incidentType")}
                </DotLegalHeader>
                <DotLegalMultiSelect
                    options={incidentTypes}
                    chosenOptions={saveModel.types}
                    onChange={(types) => {
                        let tempViewModel = { ...saveModel };
                        tempViewModel.types = types;
                        setSaveModel(tempViewModel);
                    }}
                    placeholder={translateString("incidentTypeDropdown")}
                    label={""}
                    noOptionsLabel={""}
                    noMargin
                />
            </Box>
            <Box sx={styles.subContainer} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentDataCategoryDescription")}>
                    {translateString("incidentDataCategory")}
                </DotLegalHeader>
                <DotLegalMultiSelect
                    options={dataCategoriesData}
                    chosenOptions={saveModel?.dataCategories}
                    onChange={(types) => {
                        let tempModel = { ...saveModel };
                        tempModel.dataCategories = types;
                        setSaveModel(tempModel);
                    }}
                    placeholder={translateString("incidentDataCategorySelectorPlaceholder")}
                    label={""}
                    noOptionsLabel={""}
                    alwaysOpenInBottom
                    listMaxHeight={190}
                    noMargin
                />
            </Box>
            <Box sx={styles.subContainer} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentSeverityLevelDescription")}>
                    {translateString("severity")}
                </DotLegalHeader>
                <DotLegalSelect
                    selectedItem={saveModel.severity?.toString() ?? null}
                    options={severityLevelSelectOptions}
                    onChange={(severity) => {
                        let tempModel = { ...saveModel };
                        tempModel.severity = Number(severity);
                        setSaveModel(tempModel);
                    }}
                    label={""}
                    noOptionsLabel={""}
                    alwaysOpenInBottom
                    listMaxHeight={190}
                    noMargin
                    disableClearable
                />
            </Box>
            <Box sx={{ ...styles.subContainer, mb: 4 }} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentConsequenceDescription")}>
                    {translateString("incidentConsequence")}
                </DotLegalHeader>
                <DotLegalTextFieldWithSnippets
                    snippets={consequenceSuggestions}
                    debounce={false}
                    value={saveModel?.consequences}
                    label={""}
                    variant={"outlined"}
                    error={""}
                    placeholder={translateString("incidentConsequenceTextFieldPlaceholder")}
                    rows={props.isPdf ? undefined : 5}
                    onChange={(value) => {
                        let tempModel = { ...saveModel, consequences: value };
                        setSaveModel(tempModel);
                    }}
                    select={translateString("incidentConsequenceSuggestions")}
                    disableValidation
                    noMargin
                    limitHeight
                    isPdf={props.isPdf}
                />
            </Box>
            <Box sx={{ ...styles.subContainer, mb: 4 }} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={translateString("incidentMeasureDescription")}>
                    {translateString("incidentMeasure")}
                </DotLegalHeader>

                <DotLegalTextFieldWithSnippets
                    snippets={securityMeasures}
                    debounce={false}
                    value={saveModel?.measures}
                    label={""}
                    variant={"outlined"}
                    error={""}
                    placeholder={translateString("incidentMeasureTextFieldPlaceholder")}
                    rows={5}
                    onChange={(value) => {
                        let tempModel = { ...saveModel, measures: value };
                        setSaveModel(tempModel);
                    }}
                    select={translateString("incidentMeasureSuggestions")}
                    disableValidation
                    noMargin
                    limitHeight
                    isPdf={props.isPdf}
                />
            </Box>
            <Box sx={styles.subContainer} className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                    {translateString("incidentAgencyReport")}
                </DotLegalHeader>
                <RadioGroup
                    row
                    value={saveModel?.reportedToAgency === null ? null : saveModel?.reportedToAgency ? 1 : 0}
                    onChange={(e) => {
                        let tempModel = { ...saveModel };
                        const notified = Number((e.target as HTMLInputElement).value) === 1;
                        tempModel.reportedToAgency = notified;
                        if (notified) {
                            tempModel.reasonForNotReportingToAgency = null;
                        } else {
                            tempModel.dateOfReportedToAgency = null;
                        }

                        setSaveModel(tempModel);
                    }}
                >
                    <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("yes")} />
                    <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("no")} />
                </RadioGroup>
                {saveModel?.reportedToAgency !== null &&
                    (saveModel?.reportedToAgency ? (
                        <Box>
                            <DotLegalHeader marginBottom={1} headerStyle={"extraSmall"}>
                                {translateString("incidentAgencyReportDate")}
                            </DotLegalHeader>

                            <DotLegalDatePicker
                                label={""}
                                noMargin
                                value={saveModel?.dateOfReportedToAgency}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel, dateOfReportedToAgency: value };
                                    setSaveModel(tempModel);
                                }}
                                minDate={getMinDate()}
                                maxDate={getMaxDate()}
                                language={datePickerLanguage}
                            />
                        </Box>
                    ) : (
                        <Box className={"avoid-page-break"}>
                            <DotLegalHeader marginBottom={1} headerStyle={"extraSmall"}>
                                {translateString("incidentAgencyReportReason")}
                            </DotLegalHeader>
                            <DotLegalTextField
                                label={""}
                                value={saveModel?.reasonForNotReportingToAgency}
                                debounce={false}
                                multiline
                                rows={props.isPdf ? undefined : 3}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel, reasonForNotReportingToAgency: value };
                                    setSaveModel(tempModel);
                                }}
                                noMargin
                                isPdf={props.isPdf}
                            />
                        </Box>
                    ))}
            </Box>
            <Box className={"avoid-page-break"}>
                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                    {translateString("incidentAffectedPeopleNotified")}
                </DotLegalHeader>
                <RadioGroup
                    row
                    value={saveModel?.affectedPeopleNotified === null ? null : saveModel?.affectedPeopleNotified ? 1 : 0}
                    onChange={(e) => {
                        let tempModel = { ...saveModel };
                        const notified = Number((e.target as HTMLInputElement).value) === 1;
                        tempModel.affectedPeopleNotified = notified;
                        if (notified) {
                            tempModel.reasonForNotNotifyingAffectedPeople = null;
                        } else {
                            tempModel.dateOfAffectedPeopleNotified = null;
                        }
                        setSaveModel(tempModel);
                    }}
                >
                    <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("yes")} />
                    <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("no")} />
                </RadioGroup>
                {saveModel?.affectedPeopleNotified !== null &&
                    (saveModel?.affectedPeopleNotified ? (
                        <Box className={"avoid-page-break"}>
                            <DotLegalHeader marginBottom={1} headerStyle={"extraSmall"}>
                                {translateString("incidentAffectedPeopleNotifiedDate")}
                            </DotLegalHeader>

                            <DotLegalDatePicker
                                label={""}
                                noMargin
                                value={saveModel?.dateOfAffectedPeopleNotified}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel, dateOfAffectedPeopleNotified: value };
                                    setSaveModel(tempModel);
                                }}
                                minDate={getMinDate()}
                                maxDate={getMaxDate()}
                                language={datePickerLanguage}
                            />
                        </Box>
                    ) : (
                        <Box className={"avoid-page-break"}>
                            <DotLegalHeader marginBottom={1} headerStyle={"extraSmall"}>
                                {translateString("incidentAffectedPeopleNotifiedReason")}
                            </DotLegalHeader>
                            <DotLegalTextField
                                label={""}
                                value={saveModel?.reasonForNotNotifyingAffectedPeople}
                                debounce={false}
                                multiline
                                rows={props.isPdf ? undefined : 3}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel, reasonForNotNotifyingAffectedPeople: value };
                                    setSaveModel(tempModel);
                                }}
                                noMargin
                                isPdf={props.isPdf}
                            />
                        </Box>
                    ))}
            </Box>

            {!isEdit() && (
                <>
                    <DotLegalHeader marginTop={3} marginBottom={0} headerStyle={"small"}>
                        {translateString("uploadDocumentation")}
                    </DotLegalHeader>

                    <RadioGroup
                        row
                        value={showAddDocument ? 1 : 0}
                        onChange={(e) => {
                            let temp = { ...documentUploadModel };
                            const show = Number((e.target as HTMLInputElement).value) === 1;
                            if (!show) {
                                temp.files = [];
                                temp.link = "";
                            }
                            setShowAddDocument(show);
                            setDocumentUploadModel(temp);
                        }}
                    >
                        <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("yes")} />
                        <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("no")} />
                    </RadioGroup>

                    {showAddDocument && (
                        <Box ref={bottomRef}>
                            <DotLegalTextField
                                label={translateString("name")}
                                value={saveModel?.document?.name!}
                                debounce={false}
                                errorText={validation?.getErrorsForDisplay("document")}
                                onChange={(value) => {
                                    let tempModel = { ...saveModel };
                                    tempModel.document!.name = value;
                                    setSaveModel(tempModel);
                                }}
                            />
                            <Box mt={2} mb={2}>
                                <DotLegalDocumentUploadWrapper
                                    showError={validation.showErrors}
                                    viewModel={documentUploadModel}
                                    onDataChange={(model) => {
                                        setDocumentUploadModel(model);
                                        setDocumentName(model.files);
                                    }}
                                />
                                <UploadProgressBar isUploading={isUploadingDocument} uploadPercentage={uploadPercentage} />
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {incidentLogPlanLimit?.data?.warnLimitReachedOnTrial && !isEdit() && (
                <TrialUsageWarning textKey={"createExceedingNumberOfIncidentWarning"} marginTop={2} />
            )}
        </Box>
    );

    if (!props.isPdf) {
        content = (
            <DotLegalDialog
                open
                header={isEdit() ? translateString("incidentDialogEditHeader") : translateString("incidentDialogCreateHeader")}
                onDialogClose={props.closeDialog}
                size={"md"}
                buttonOkText={translateString("save")}
                onOkClick={() => {
                    validateAndSave();
                }}
                isLoading={isLoading}
                inProgress={isSaving}
                disableBackdropOnCloseIfValueChange={saveModel}
            >
                {content}
            </DotLegalDialog>
        );
    }

    return content;
}

export default CreateEditIncidentLogDialog;
